/* eslint-disable react/jsx-no-bind */
import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'antd';
import { DeveloperModal } from 'widgets/roles-modal/ui/DeveloperModal/DeveloperModal';
import { InternalAccountModal } from 'widgets/roles-modal/ui/InternalAccountModal/InternalAccountModal';
import { UnauthorizedModal } from 'widgets/roles-modal/ui/UnauthorizedModal/UnauthorizedModal';
import { UsageOnlyModal } from 'widgets/roles-modal/ui/UsageOnlyModal/UsageOnlyModal';

import { useApplicationUserQuery } from 'entities/application/api'; // applicationApi
import { useApplication } from 'entities/application/modal/useApplication';
import { Plan } from 'entities/application/types';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { DeleteApplication } from 'features/application/delete/ui/DeleteApplication/DeleteApplication';
import { GoToApp } from 'features/application/go-to/ui/GoToApp/GoToApp';
import { SetupApplication } from 'features/application/install/ui/SetupApplication/SetupApplication';
import { GoToTariffs } from 'features/navigation/ui/GoToTariffs/GoToTariffs';
import { baseUrl } from 'shared/constants';
import { ModalFooter } from 'shared/ui/ModalFooter';
import { MpButton } from 'shared/ui/MpButton';
import { BUttonsContainerStyled } from './ApplicationActions.styles';

type ApplicationActionsProps = {
	applicationId: string;
	handleInstall: () => void;
	handleDelete: () => void;
	isInstalled: boolean;
	userId: string;
	hasActiveSubscriptions: boolean;
	redirectUrl: string;
	plans: Plan[] | null;
	isDeveloper: boolean;
	changeTabToTariffs: () => void;
	isDownloadable: undefined | boolean;
	isOpenForUnauthorized: boolean;
	isExternal: boolean;
	hasSetupSupport?: boolean;
	hasAdditionalFlow?: FunctionComponent<{ isInstalled: boolean; handleInstall?: () => Promise<void> }>;
	applicationName: string;
};

export const ApplicationActions: React.FC<ApplicationActionsProps> = ({
	applicationId,
	handleInstall,
	handleDelete,
	changeTabToTariffs,
	isInstalled,
	applicationName,
	userId,
	hasActiveSubscriptions,
	redirectUrl,
	plans,
	isExternal,
	isDeveloper,
	isDownloadable,
	isOpenForUnauthorized,
	hasSetupSupport,
	hasAdditionalFlow,
}: // FIXME
// eslint-disable-next-line sonarjs/cognitive-complexity
ApplicationActionsProps) => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const application = useApplication(applicationId);
	const authorization = useAuthorization();
	const internalAccount = authorization?.blockAppInstallation;
	const { data: applicationUserInfo } = useApplicationUserQuery(applicationId);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { t } = useTranslation();

	const closeModal = (): void => {
		setIsModalOpen(false);
	};

	const openModal = (): void => {
		setIsModalOpen(true);
	};

	const unauthorizedFooter = [
		<ModalFooter key='1'>
			<span>
				<Button key='back' size='large' onClick={closeModal} block>
					{t(`Marketplace_Cancel`)}
				</Button>
			</span>
			<a
				key='login-link'
				href={`${baseUrl}/privateapi/auth/externallogin?provider=mpoidc&returnUrl=${window.location.href}`}
			>
				<Button key='login' size='large' type='primary' block>
					{t(`Marketplace_Sign_In_Dodo_IS`)}
				</Button>
			</a>
		</ModalFooter>,
	];

	const developerFooter = [
		<ModalFooter key='1'>
			<span>
				<Button key='back' size='large' onClick={closeModal} block>
					{t(`Marketplace_Cancel`)}
				</Button>
			</span>
			<a
				key='login-link'
				href={`${baseUrl}/privateapi/auth/externallogin?provider=mpoidc&returnUrl=/externalLogin`}
			>
				<Button key='login' size='large' type='primary' block>
					{t(`Marketplace_Sign_In_Dodo_IS`)}
				</Button>
			</a>
		</ModalFooter>,
	];

	const authorizedFooter = [
		<ModalFooter key='1'>
			<span>
				<Button key='back' size='large' onClick={closeModal} block>
					{t(`Marketplace_Cancel`)}
				</Button>
			</span>
			<a
				key='login-link'
				href={`/privateapi/auth/externallogin?provider=mpoidc&returnUrl=${window.location.href}`}
			>
				<Button key='login' size='large' type='primary' block>
					{t(`Marketplace_Sign_Out_Dodo_IS`)}
				</Button>
			</a>
		</ModalFooter>,
	];

	if (isOpenForUnauthorized) {
		return (
			<a href={redirectUrl} target='_blank' rel='noreferrer'>
				<MpButton size='large' type='primary' fullWidth>
					{t(`Marketplace_Install`)}
				</MpButton>
			</a>
		);
	}

	if (!authorization) {
		return (
			<>
				<Modal
					className='roles-modal'
					width={690}
					open={isModalOpen}
					onCancel={closeModal}
					footer={unauthorizedFooter}
				>
					<UnauthorizedModal />
				</Modal>
				{plans ? (
					<GoToTariffs handleClick={changeTabToTariffs} />
				) : (
					<MpButton onClick={openModal} type='primary' block size='large'>
						{t(`Marketplace_Sign_In_And_Connect`)}
					</MpButton>
				)}
			</>
		);
	}

	if (isExternal && internalAccount) {
		return (
			<>
				<Modal
					width={690}
					className='roles-modal'
					open={isModalOpen}
					onCancel={closeModal}
					footer={authorizedFooter}
				>
					<InternalAccountModal />
				</Modal>
				{plans ? (
					<GoToTariffs handleClick={changeTabToTariffs} />
				) : (
					<MpButton onClick={openModal} type='primary' block size='large'>
						{t(`Marketplace_Install`)}
					</MpButton>
				)}
			</>
		);
	}

	if (isDeveloper) {
		return (
			<>
				<Modal
					className='roles-modal'
					width={690}
					open={isModalOpen}
					onCancel={closeModal}
					footer={developerFooter}
				>
					<DeveloperModal />
				</Modal>
				<MpButton onClick={openModal} type='primary' block size='large'>
					{t(`Marketplace_Install`)}
				</MpButton>
			</>
		);
	}

	if (!applicationUserInfo || !application) {
		return null;
	}

	const onConnectedUrl: string | null = application.onConnectedUrl ?? redirectUrl;
	const onDeletedUrl: string | null = application.onDeletedUrl ? application.onDeletedUrl + userId : ``;

	if (isInstalled || applicationUserInfo.status === `Installed`) {
		return (
			<BUttonsContainerStyled>
				<GoToApp
					redirectLink={application.onConnectedUrl ? application.onConnectedUrl : redirectUrl}
					isDownloadable={isDownloadable}
					hasAdditionalFlow={hasAdditionalFlow}
					applicationId={applicationId}
					applicationName={applicationName}
				/>
				<DeleteApplication
					applicationId={applicationId}
					handleButtonClick={handleDelete}
					redirectUrl={onDeletedUrl}
				/>
			</BUttonsContainerStyled>
		);
	}

	return (
		<>
			<Modal
				width={690}
				className='roles-modal'
				open={isModalOpen}
				onCancel={closeModal}
				footer={authorizedFooter}
			>
				<UsageOnlyModal />
			</Modal>
			{plans && (!hasActiveSubscriptions || internalAccount) ? (
				<GoToTariffs handleClick={changeTabToTariffs} />
			) : (
				<SetupApplication
					borderRadius
					applicationName={applicationName}
					applicationId={applicationId}
					handleInstall={handleInstall}
					redirectUrl={onConnectedUrl}
					isLink={false}
					isDownloadable={isDownloadable}
					disabled={isExternal && internalAccount}
					hasSetupSupport={hasSetupSupport}
					hasAdditionalFlow={hasAdditionalFlow}
				/>
			)}
		</>
	);
};
